import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Alert,
  Box,
  Button,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  LinearProgress,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';

import { withdrawalsApi, type CommissionChange } from '../api/withdrawals.api.js';
import { commissionInput, parseCommission } from '../utils/commission-money.js';
import { formatTransactionAmount as money } from '../utils/transaction-money.js';
import { APP_LOCALE } from '../utils/locale.js';

/**
 * Staff control over the commission on money going out.
 *
 * The same two moves as the incoming panel — edit before the charge, correct
 * after it — with one thing the incoming screen has no need for: the amount the
 * customer actually authorised stays on screen. They approved a total with an
 * authenticator code, and an officer changing it should be looking at what they
 * are departing from.
 */
export function OutgoingCommissionPanel({ withdrawalId, canEditCommission = false, canEditAmount = false }: {
  withdrawalId: string;
  /** DEV-67: admin and the client manager set a commission; others see it. */
  canEditCommission?: boolean;
  /** Correcting the instruction amount is part of review (admin, AML / MLRO). */
  canEditAmount?: boolean;
}): JSX.Element {
  const cache = useQueryClient();
  const query = useQuery({
    queryKey: ['outgoing-commission', withdrawalId],
    queryFn: () => withdrawalsApi.commission(withdrawalId),
  });
  const [draft, setDraft] = useState<null | {
    mode: 'edit' | 'correct';
    operationId: string;
    expectedRevision: number;
    expectedFeeCents: string;
    agreedFeeCents: string | null;
    currency: string;
    amountCents: string;
  }>(null);
  const [amount, setAmount] = useState('');
  const [reason, setReason] = useState('');
  // The instruction amount is a separate decision from the commission, so it
  // gets its own draft rather than sharing one reason box with it.
  const [amountDraft, setAmountDraft] = useState<null | {
    operationId: string;
    expectedRevision: number;
    expectedAmountCents: string;
    currency: string;
  }>(null);
  const [newAmount, setNewAmount] = useState('');
  const [amountReason, setAmountReason] = useState('');
  const [message, setMessage] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  const refresh = async (): Promise<void> => {
    await Promise.all([
      cache.invalidateQueries({ queryKey: ['outgoing-commission', withdrawalId] }),
      cache.invalidateQueries({ queryKey: ['withdrawal-detail', withdrawalId] }),
      cache.invalidateQueries({ queryKey: ['withdrawals'] }),
      cache.invalidateQueries({ queryKey: ['transactions'] }),
      cache.invalidateQueries({ queryKey: ['commissions'] }),
    ]);
  };

  const changeAmount = useMutation({
    mutationFn: async (payload: {
      draft: NonNullable<typeof amountDraft>;
      input: { amountCents: string; expectedAmountCents: string; expectedRevision: number; reason: string };
    }) => withdrawalsApi.editAmount(withdrawalId, payload.input, payload.draft.operationId),
    onSuccess: async () => {
      setAmountDraft(null);
      setMessage('Amount corrected. Nothing has moved — it is charged when the payment is approved.');
      await refresh();
    },
    onError: async (err) => {
      setAmountDraft(null);
      setError(err instanceof Error ? err.message : 'The amount could not be changed.');
      await refresh();
    },
  });

  const change = useMutation({
    mutationFn: async (payload: { draft: NonNullable<typeof draft>; input: CommissionChange }) => {
      if (payload.draft.mode === 'edit')
        await withdrawalsApi.editFee(withdrawalId, payload.input, payload.draft.operationId);
      else await withdrawalsApi.correctFee(withdrawalId, payload.input, payload.draft.operationId);
    },
    onSuccess: async (_value, payload) => {
      setDraft(null);
      setMessage(
        payload.draft.mode === 'edit'
          ? 'Commission updated. It will be charged when the payment is approved.'
          : 'Commission corrected. The adjustment is recorded in transaction history.',
      );
      await refresh();
    },
    onError: async (err) => {
      setDraft(null);
      setError(err instanceof Error ? err.message : 'The commission could not be confirmed.');
      await refresh();
    },
  });

  const resume = useMutation({
    mutationFn: (operationId: string) => withdrawalsApi.resumeCommission(withdrawalId, operationId),
    onSuccess: async () => {
      setMessage('The saved operation is confirmed.');
      setError(null);
      await refresh();
    },
    onError: async (err) => {
      setError(err instanceof Error ? err.message : 'The operation is still awaiting confirmation.');
      await refresh();
    },
  });

  const data = query.data;
  const open = (mode: 'edit' | 'correct'): void => {
    if (!data) return;
    setError(null);
    setMessage(null);
    setReason('');
    setAmount(commissionInput(data.feeCents, data.currency));
    setDraft({
      mode,
      operationId: crypto.randomUUID(),
      expectedRevision: mode === 'edit' ? data.editRevision : (data.charged?.revision ?? 0),
      expectedFeeCents: data.feeCents,
      agreedFeeCents: data.agreedFeeCents,
      currency: data.currency,
      amountCents: data.amountCents,
    });
  };

  // Parsed with the same exact-minor-unit rules as the commission: a payment
  // amount that went through a float is not the amount anybody agreed.
  let amountPreview: bigint | null = null;
  let amountValidation: string | null = null;
  if (amountDraft) {
    try {
      amountPreview = parseCommission(newAmount, amountDraft.currency);
      if (amountPreview <= 0n) amountValidation = 'A payment has to be for more than nothing.';
      else if (amountPreview.toString() === amountDraft.expectedAmountCents)
        amountValidation = 'That is the amount it already holds.';
    } catch (err) {
      amountValidation = err instanceof Error ? err.message : 'Enter a valid amount.';
    }
  }

  let preview: bigint | null = null;
  let validation: string | null = null;
  if (draft) {
    try {
      preview = parseCommission(amount, draft.currency);
      if (preview > BigInt(draft.amountCents))
        validation = 'The commission cannot exceed the payment amount.';
    } catch (err) {
      validation = err instanceof Error ? err.message : 'Enter a valid commission.';
    }
  }
  const delta = preview !== null && draft ? preview - BigInt(draft.expectedFeeCents) : null;
  // Charging above what the customer authorised is allowed here, but it is not
  // something an officer should be able to do without noticing.
  const aboveAgreed =
    preview !== null && draft?.agreedFeeCents !== null && draft?.agreedFeeCents !== undefined
      ? preview - BigInt(draft.agreedFeeCents)
      : null;

  return (
    <Box sx={{ px: { xs: 2.5, sm: 3 }, py: 2, borderTop: 1, borderColor: 'divider' }}>
      <Stack spacing={1.5}>
        <Stack direction="row" justifyContent="space-between" alignItems="center" gap={1}>
          <Typography variant="subtitle1" fontWeight={700}>
            Transaction commission
          </Typography>
          {data && (
            <Chip
              size="small"
              label={
                data.pendingOperationId
                  ? 'Awaiting confirmation'
                  : data.verificationError
                    ? 'Unverified'
                    : data.charged?.refunded
                      ? 'Refunded'
                      : data.charged
                        ? 'Charged'
                        : 'Before charge'
              }
            />
          )}
        </Stack>
        {query.isLoading && <LinearProgress aria-label="Loading commission" />}
        {query.isError && (
          <Alert
            severity="error"
            action={
              <Button
                onClick={() => {
                  void query.refetch();
                }}
              >
                Retry
              </Button>
            }
          >
            Commission details could not be loaded.
          </Alert>
        )}
        {message && (
          <Alert severity="success" onClose={() => setMessage(null)}>
            {message}
          </Alert>
        )}
        {error && (
          <Alert severity="error" onClose={() => setError(null)}>
            {error}
          </Alert>
        )}
        {data && (
          <>
            <Typography variant="h6">{money(data.feeCents, data.currency)}</Typography>
            <Stack spacing={1} sx={{ mb: 1 }}>
              <Typography variant="body2">
                Sending {money(data.amountCents, data.currency)}
                {data.editable && canEditAmount && (
                  <Button
                    size="small"
                    sx={{ ml: 1 }}
                    onClick={() => {
                      setError(null);
                      setMessage(null);
                      setAmountReason('');
                      setNewAmount(commissionInput(data.amountCents, data.currency));
                      setAmountDraft({
                        operationId: crypto.randomUUID(),
                        expectedRevision: data.amountEditRevision,
                        expectedAmountCents: data.amountCents,
                        currency: data.currency,
                      });
                    }}
                  >
                    Correct the amount
                  </Button>
                )}
              </Typography>
              {data.originalAmountCents !== null &&
                data.originalAmountCents !== undefined &&
                data.originalAmountCents !== data.amountCents && (
                  <Alert severity="info">
                    The customer asked to send{' '}
                    {money(data.originalAmountCents, data.currency)} and authorised that under
                    strong customer authentication. Staff have corrected the amount since.
                  </Alert>
                )}
              {/* The commission is a percentage of the amount, so moving one
                  leaves the other priced against a figure that no longer
                  exists. Nothing re-prices it automatically -- that is money
                  nobody approved -- so the officer is told and decides. */}
              {data.feeQuotedForCents !== null &&
                data.feeQuotedForCents !== undefined &&
                data.feeQuotedForCents !== data.amountCents && (
                  <Alert severity="warning">
                    This commission was priced against{' '}
                    {money(data.feeQuotedForCents, data.currency)}, not the amount now being
                    sent. Check it before approving.
                  </Alert>
                )}
            </Stack>
            {/* An absent value is not a mismatch. Treating it as one put
                money(undefined) on screen, which threw and blanked the whole
                console in the middle of a payment review. */}
            {data.agreedFeeCents !== null &&
              data.agreedFeeCents !== undefined &&
              data.agreedFeeCents !== data.feeCents && (
              <Alert severity="info">
                The customer authorised {money(data.agreedFeeCents, data.currency)} under strong
                customer authentication. This payment carries a staff-set commission instead.
              </Alert>
            )}
            {data.charged ? (
              <Typography variant="body2" color="text.secondary" sx={{ overflowWrap: 'anywhere' }}>
                Charged {money(data.charged.originalFeeCents, data.currency)} at approval · fee
                account {data.charged.feeAccountId}
              </Typography>
            ) : (
              <Typography variant="body2" color="text.secondary">
                {data.overrideFeeCents !== null
                  ? 'Staff-set commission. This amount will be charged on approval.'
                  : 'The commission the customer agreed to. It is charged when this payment is approved.'}
              </Typography>
            )}
            {data.verificationError && <Alert severity="warning">{data.verificationError}</Alert>}
            {data.pendingOperationId ? (
              <Alert
                severity="warning"
                action={!canEditCommission ? undefined :
                  <Button
                    disabled={resume.isPending}
                    onClick={() => resume.mutate(data.pendingOperationId!)}
                  >
                    Confirm outcome
                  </Button>
                }
              >
                A saved operation is awaiting confirmation. It will reuse the original instruction.
              </Alert>
            ) : data.editable && canEditCommission ? (
              <Button
                variant="outlined"
                onClick={() => open('edit')}
                sx={{ alignSelf: 'flex-start' }}
              >
                Edit commission before charge
              </Button>
            ) : canEditCommission && data.charged && !data.charged.refunded ? (
              <Button
                variant="outlined"
                onClick={() => open('correct')}
                sx={{ alignSelf: 'flex-start' }}
              >
                Correct charged commission
              </Button>
            ) : null}
            {(data.history.length > 0 || (data.charged?.changes.length ?? 0) > 0) && (
              <Accordion disableGutters elevation={0}>
                <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                  <Typography variant="body2">Commission history</Typography>
                </AccordionSummary>
                <AccordionDetails>
                  <Stack spacing={1.5}>
                    {data.history.map((item) => {
                      const chargedChange = data.charged?.changes.find(
                        (c) => c.operationId === item.id,
                      );
                      return (
                        <Box key={item.id}>
                          <Typography variant="body2" fontWeight={600}>
                            {item.kind === 'EDIT' ? 'Edit before charge' : 'Charged correction'} ·{' '}
                            {item.status}
                          </Typography>
                          {item.request && (
                            <Typography variant="body2">
                              {money(item.request.expectedFeeCents, data.currency)} →{' '}
                              {money(item.request.feeCents, data.currency)}
                            </Typography>
                          )}
                          {chargedChange && (
                            <Typography variant="body2">
                              {money(chargedChange.beforeCents, data.currency)} →{' '}
                              {money(chargedChange.afterCents, data.currency)}
                            </Typography>
                          )}
                          <Typography variant="body2">{item.reason}</Typography>
                          <Typography
                            variant="caption"
                            color="text.secondary"
                            sx={{ overflowWrap: 'anywhere' }}
                          >
                            {new Date(item.createdAt).toLocaleString(APP_LOCALE)} · {item.officerId}
                          </Typography>
                        </Box>
                      );
                    })}
                  </Stack>
                </AccordionDetails>
              </Accordion>
            )}
          </>
        )}
      </Stack>

      <Dialog
        open={amountDraft !== null}
        onClose={() => {
          if (!changeAmount.isPending) setAmountDraft(null);
        }}
        fullWidth
        maxWidth="sm"
      >
        <Box
          component="form"
          onSubmit={(event) => {
            event.preventDefault();
            if (!amountDraft || amountPreview === null || amountValidation !== null) return;
            if (amountReason.trim().length < 4) return;
            changeAmount.mutate({
              draft: amountDraft,
              input: {
                amountCents: amountPreview.toString(),
                expectedAmountCents: amountDraft.expectedAmountCents,
                expectedRevision: amountDraft.expectedRevision,
                reason: amountReason.trim(),
              },
            });
          }}
        >
          <DialogTitle>Correct the amount being sent</DialogTitle>
          <DialogContent>
            <Stack spacing={2} sx={{ pt: 1 }}>
              <Typography variant="body2">
                This changes what the customer asked us to send. They authorised the original
                under strong customer authentication, so it stays on the record. Nothing moves
                until the payment is approved.
              </Typography>
              <TextField
                label={`New amount (${amountDraft?.currency ?? ''})`}
                value={newAmount}
                onChange={(e) => setNewAmount(e.target.value)}
                inputProps={{ inputMode: 'decimal' }}
                required
                error={amountValidation !== null}
                helperText={amountValidation ?? 'The amount the beneficiary should receive.'}
              />
              <TextField
                label="Why"
                value={amountReason}
                onChange={(e) => setAmountReason(e.target.value)}
                multiline
                minRows={2}
                required
                inputProps={{ maxLength: 1000 }}
                helperText="Recorded against the payment with your name."
              />
              {amountDraft && amountPreview !== null && amountValidation === null && (
                <Alert severity="info">
                  Was {money(amountDraft.expectedAmountCents, amountDraft.currency)}, becomes{' '}
                  {money(amountPreview.toString(), amountDraft.currency)}. The commission is
                  charged separately and is not re-priced by this.
                </Alert>
              )}
            </Stack>
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setAmountDraft(null)} disabled={changeAmount.isPending}>
              Cancel
            </Button>
            <Button
              type="submit"
              variant="contained"
              disabled={
                changeAmount.isPending ||
                amountPreview === null ||
                amountValidation !== null ||
                amountReason.trim().length < 4
              }
            >
              Change the amount
            </Button>
          </DialogActions>
        </Box>
      </Dialog>

      <Dialog
        open={draft !== null}
        onClose={() => {
          if (!change.isPending) setDraft(null);
        }}
        fullWidth
        maxWidth="sm"
      >
        <Box
          component="form"
          onSubmit={(event) => {
            event.preventDefault();
            if (!draft || preview === null || validation || reason.trim().length < 4) return;
            change.mutate({
              draft,
              input: {
                feeCents: preview.toString(),
                expectedFeeCents: draft.expectedFeeCents,
                expectedRevision: draft.expectedRevision,
                reason: reason.trim(),
              },
            });
          }}
        >
          <DialogTitle>
            {draft?.mode === 'edit'
              ? 'Edit commission before charge'
              : 'Correct charged commission'}
          </DialogTitle>
          <DialogContent>
            <Stack spacing={2} sx={{ pt: 1 }}>
              <Typography variant="body2">
                {draft?.mode === 'edit'
                  ? 'This changes the commission for this payment. No money moves until it is approved.'
                  : 'The original charge remains in history. Only the difference will be charged or refunded to the recorded fee account.'}
              </Typography>
              <TextField
                label={`New commission (${draft?.currency ?? ''})`}
                value={amount}
                onChange={(e) => setAmount(e.target.value)}
                inputProps={{ inputMode: 'decimal' }}
                required
                error={validation !== null}
                helperText={
                  validation ??
                  'Enter the final total commission, including any amount already charged.'
                }
              />
              {draft && preview !== null && !validation && (
                <>
                  <Divider />
                  <Typography variant="body2">
                    Total debit:{' '}
                    <strong>
                      {money((BigInt(draft.amountCents) + preview).toString(), draft.currency)}
                    </strong>
                  </Typography>
                  {aboveAgreed !== null && aboveAgreed > 0n && (
                    <Alert severity="warning">
                      This is {money(aboveAgreed.toString(), draft.currency)} more than the customer
                      authorised. They will be charged an amount they did not approve, and your
                      name and reason are recorded against it.
                    </Alert>
                  )}
                  {draft.mode === 'correct' && delta !== null && (
                    <Alert severity={delta > 0n ? 'warning' : 'info'}>
                      {delta > 0n ? 'Additional charge' : delta < 0n ? 'Refund' : 'No change'}:{' '}
                      {money((delta < 0n ? -delta : delta).toString(), draft.currency)}
                    </Alert>
                  )}
                </>
              )}
              <TextField
                label="Reason for commission change"
                value={reason}
                onChange={(e) => setReason(e.target.value)}
                required
                multiline
                minRows={2}
                inputProps={{ minLength: 4, maxLength: 1000 }}
              />
            </Stack>
          </DialogContent>
          <DialogActions>
            <Button disabled={change.isPending} onClick={() => setDraft(null)}>
              Cancel
            </Button>
            <Button
              type="submit"
              variant="contained"
              disabled={
                change.isPending ||
                !!validation ||
                preview === null ||
                reason.trim().length < 4 ||
                (draft?.mode === 'correct' && delta === 0n)
              }
            >
              {change.isPending
                ? 'Saving…'
                : draft?.mode === 'edit'
                  ? 'Save commission'
                  : 'Post correction'}
            </Button>
          </DialogActions>
        </Box>
      </Dialog>
    </Box>
  );
}
